// purpose: mobileの衝撃リングと収束線を、事前構築GraphicsContextと固定Spriteだけで描く。combat中のpath再構築を行わない。

import {
  Container,
  Graphics,
  GraphicsContext,
  Sprite,
  Texture,
} from 'pixi.js';
import { R2_HEIGHT, R2_WIDTH } from '../R2Renderer';

const SHOCK_RING_SLOTS = 3;
const RING_RADIUS_MIN = 8;
const RING_RADIUS_MAX = 90;
const RING_WIDTH_COUNT = 2;
const BORDER_INSET_MAX = 34;
const BORDER_WIDTH_MIN = 2;
const BORDER_WIDTH_COUNT = 2;
const WHITE = 0xffffff;
const IMPACT_BAR_COLOR = 0xd9fbff;
const IMPACT_BORDER_COLOR = 0x83f5ff;

/**
 * Pixi tessellates each immutable context once. Runtime work is limited to
 * context selection plus transform/tint/alpha updates on seven fixed leaves.
 */
export class MobileImpactPrimitives {
  readonly container = new Container();

  private readonly ringContexts: GraphicsContext[] = [];
  private readonly borderContexts: GraphicsContext[] = [];
  private readonly rings: Graphics[] = [];
  private readonly bars: Sprite[] = [];
  private readonly border: Graphics;

  constructor() {
    for (let radius = RING_RADIUS_MIN; radius <= RING_RADIUS_MAX; radius += 1) {
      for (let width = 1; width <= RING_WIDTH_COUNT; width += 1) {
        const context = new GraphicsContext()
          .circle(0, 0, radius)
          .stroke({ color: WHITE, alpha: 1, width });
        context.autoGarbageCollect = false;
        this.ringContexts.push(context);
      }
    }
    for (let inset = 0; inset <= BORDER_INSET_MAX; inset += 1) {
      for (let widthOffset = 0; widthOffset < BORDER_WIDTH_COUNT; widthOffset += 1) {
        const width = BORDER_WIDTH_MIN + widthOffset;
        const context = new GraphicsContext()
          .rect(inset, inset, R2_WIDTH - inset * 2, R2_HEIGHT - inset * 2)
          .stroke({ color: WHITE, alpha: 1, width });
        context.autoGarbageCollect = false;
        this.borderContexts.push(context);
      }
    }
    const initialRingContext = this.ringContexts[0]!;
    for (let i = 0; i < SHOCK_RING_SLOTS; i += 1) {
      const ring = new Graphics({
        context: initialRingContext,
        roundPixels: true,
        autoGarbageCollect: false,
      });
      ring.visible = false;
      this.rings.push(ring);
      this.container.addChild(ring);
    }
    for (let i = 0; i < 4; i += 1) {
      const bar = new Sprite(Texture.WHITE);
      bar.roundPixels = true;
      bar.tint = IMPACT_BAR_COLOR;
      bar.visible = false;
      this.bars.push(bar);
      this.container.addChild(bar);
    }
    this.border = new Graphics({
      context: this.borderContexts[0]!,
      roundPixels: true,
      autoGarbageCollect: false,
    });
    this.border.tint = IMPACT_BORDER_COLOR;
    this.border.visible = false;
    this.container.addChild(this.border);
  }

  beginFrame(): void {
    for (let i = 0; i < this.rings.length; i += 1) this.rings[i]!.visible = false;
    for (let i = 0; i < this.bars.length; i += 1) this.bars[i]!.visible = false;
    this.border.visible = false;
  }

  setShockRing(
    slot: number,
    x: number,
    y: number,
    radius: number,
    color: number,
    alpha: number,
    width: number,
  ): void {
    const ring = this.rings[slot];
    if (!ring) return;
    const roundedRadius = Math.max(RING_RADIUS_MIN, Math.min(RING_RADIUS_MAX, Math.round(radius)));
    const roundedWidth = width >= 2 ? 2 : 1;
    ring.context = this.ringContexts[
      (roundedRadius - RING_RADIUS_MIN) * RING_WIDTH_COUNT + roundedWidth - 1
    ]!;
    ring.position.set(Math.round(x), Math.round(y));
    ring.tint = color;
    ring.alpha = alpha;
    ring.visible = alpha > 0;
  }

  setImpactCompression(
    x: number,
    y: number,
    gap: number,
    inset: number,
    alpha: number,
    borderWidth: number,
  ): void {
    const clampedX = Math.max(0, Math.min(R2_WIDTH, Math.round(x)));
    const clampedY = Math.max(0, Math.min(R2_HEIGHT, Math.round(y)));
    const leftWidth = Math.max(0, clampedX - gap);
    const rightX = Math.min(R2_WIDTH, clampedX + gap);
    const topHeight = Math.max(0, clampedY - gap);
    const bottomY = Math.min(R2_HEIGHT, clampedY + gap);
    this.setBar(0, 0, clampedY - 1, leftWidth, 3, alpha);
    this.setBar(1, rightX, clampedY - 1, R2_WIDTH - rightX, 3, alpha);
    this.setBar(2, clampedX - 1, 0, 3, topHeight, alpha);
    this.setBar(3, clampedX - 1, bottomY, 3, R2_HEIGHT - bottomY, alpha);

    const roundedInset = Math.max(0, Math.min(BORDER_INSET_MAX, Math.round(inset)));
    const widthOffset = borderWidth >= 3 ? 1 : 0;
    this.border.context = this.borderContexts[roundedInset * BORDER_WIDTH_COUNT + widthOffset]!;
    this.border.alpha = alpha * 0.58;
    this.border.visible = alpha > 0;
  }

  /** Adds every explicitly owned immutable context to the init-only warm list. */
  collectWarmupContexts(target: GraphicsContext[]): void {
    for (let i = 0; i < this.ringContexts.length; i += 1) {
      target.push(this.ringContexts[i]!);
    }
    for (let i = 0; i < this.borderContexts.length; i += 1) {
      target.push(this.borderContexts[i]!);
    }
  }

  disposeContexts(): void {
    for (let i = 0; i < this.ringContexts.length; i += 1) this.ringContexts[i]!.destroy();
    for (let i = 0; i < this.borderContexts.length; i += 1) this.borderContexts[i]!.destroy();
    this.ringContexts.length = 0;
    this.borderContexts.length = 0;
    this.rings.length = 0;
    this.bars.length = 0;
  }

  private setBar(
    index: number,
    x: number,
    y: number,
    width: number,
    height: number,
    alpha: number,
  ): void {
    const bar = this.bars[index]!;
    if (width <= 0 || height <= 0 || alpha <= 0) {
      bar.visible = false;
      return;
    }
    bar.position.set(x, y);
    bar.width = width;
    bar.height = height;
    bar.alpha = alpha;
    bar.visible = true;
  }
}
